import React, { useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { useHubspot } from 'src/hooks/integrations/hubspot';
import { useIntercom } from 'src/hooks/integrations/intercom';
import { useAuthenticatedUser } from 'src/hooks/user';
import { useLicenseCheck } from 'src/hooks/license';
import { DataCollector } from '../components';
import RedirectAfterLogin from './RedirectAfterLogin';
import ACTION_ROUTES from './actions';
import ADMIN_ROUTES from './admin';
import PRIVATE_ROUTES from './private';
import PUBLIC_ROUTES from './public';
import Gallery from 'src/views/gallery';
import { Checkout, CheckoutSuccess, CheckoutCancel } from 'src/views/billing';

function ThirdPartyIntegrations() {
  useIntercom();
  useHubspot();
  return null;
}

function RouterRoot() {
  const user = useAuthenticatedUser();
  const isRegisteredUser = user && user?.role !== 'anonymous';
  const hasPendingActions = user?.actions && user.actions.length > 0;
  const { hasActiveLicense, isLoading: isLicenseLoading } = useLicenseCheck();

  // Check if teacher needs license (no pending actions, is teacher, no license)
  // Only redirect if we're sure they don't have a license (not loading, and explicitly false)
  const needsLicense = !hasPendingActions &&
                       user?.role === 'teacher' &&
                       !isLicenseLoading &&
                       hasActiveLicense !== null &&
                       hasActiveLicense === false;

  const nextActionPath = useMemo(() => {
    if (user?.actions) {
      switch (user.actions[0]) {
        case 'verify_email':
          return '/register-student';
        case 'select_role':
          return '/select-role';
        case 'add_birthdate':
          return '/birthday-form';
        case 'complete_user_data':
          return '/create-account';
        case 'add_school':
          return '/school-information-form';
        default:
          return '/gallery';
      }
    }
    return '/gallery';
  }, [user?.actions]);

  return (
    <BrowserRouter>
      <DataCollector />
      <ThirdPartyIntegrations />

      <Routes>
        {/* Billing routes - accessible even without license */}
        <Route path="/billing/checkout" element={<Checkout />} />
        <Route path="/billing/success" element={<CheckoutSuccess />} />
        <Route path="/billing/cancel" element={<CheckoutCancel />} />

        {/* Routes are limited when users have compulsory actions to complete. */}
        {hasPendingActions && (
          <>
            <Route
              path="/"
              element={<Navigate to={nextActionPath} replace />}
            />
            {ACTION_ROUTES.filter(
              (e: any) => user.actions && e.action.includes(user.actions[0]),
            ).map(({ component: Element, path, name }: any) => (
              <Route
                key={name}
                path={path}
                element={
                  <Element action={user.actions ? user.actions[0] : ''} />
                }
              />
            ))}
          </>
        )}

        <Route path={'/gallery/:tab?'} element={<Gallery />} />

        {/* Users who are not registered only get public URLs. */}
        {!hasPendingActions &&
          !isRegisteredUser &&
          PUBLIC_ROUTES.map(({ component: Element, path, name }) => (
            <Route key={name} path={path} element={<Element />} />
          ))}

        {/* Users who are registered get private URLs, but teachers need licenses */}
        {!hasPendingActions && isRegisteredUser && !needsLicense && (
          <>
            <Route path="/" element={<Navigate to="/gallery" replace />} />
            {PRIVATE_ROUTES.map(({ component: Element, path, name }) => (
              <Route key={name} path={path} element={<Element />} />
            ))}
          </>
        )}

        {/* Redirect teachers without licenses to checkout (catch-all for registered teachers) */}
        {needsLicense && (
          <Route
            path="*"
            element={<Navigate to="/billing/checkout" replace />}
          />
        )}

        {/* A small number of administrative routes are available to admins. */}
        {(user?.role === 'admin' || user?.role === 'superadmin') &&
          ADMIN_ROUTES.map(({ component: Element, path, name }) => (
            <Route key={name} path={`/admin${path}`} element={<Element />} />
          ))}

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Check whether the user should be redirected to a post-login path. */}
      <RedirectAfterLogin />
    </BrowserRouter>
  );
}

export default RouterRoot;
